import React, { useState } from 'react';
import { authApiClient } from '../../api/authApiClient';
import { handleApiError } from '../../../../shared/utils/handleApiError';
import type { LoginResponse } from '../../type';
import { AuthInput } from '../ui/AuthInput';
import Button from '../../../../shared/components/Button';

interface TwoFactorChallengeFormProps {
  /**
   * Single-use token handed back by the TWO_FACTOR_REQUIRED login response.
   * Held in the parent's component state only — never persisted, never in the
   * URL. It is bearer-equivalent for 5 minutes.
   */
  pendingToken: string;
  /** Exchange succeeded — the parent stores the session and redirects. */
  onVerified: (response: LoginResponse) => void;
  /** The pending session lapsed; the parent returns to the password step. */
  onExpired: () => void;
  /** User chose to start over rather than finish the challenge. */
  onCancel: () => void;
}

/**
 * Second step of a 2FA login (PE-2157).
 *
 * Accepts either a 6-digit TOTP code or a backup code — watchman's
 * `verifyCode` tries the authenticator secret first and falls back to the
 * single-use backup list, so one field covers both. That is why the input is
 * NOT constrained to 6 digits: backup codes are formatted `a1b2-c3d4`.
 */
export const TwoFactorChallengeForm: React.FC<TwoFactorChallengeFormProps> = ({
  pendingToken,
  onVerified,
  onExpired,
  onCancel,
}) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setServerError('Enter the code from your authenticator app.');
      return;
    }

    setIsLoading(true);
    setServerError('');
    try {
      onVerified(await authApiClient.verifyTwoFactor(pendingToken, trimmed));
    } catch (error: unknown) {
      // Both "wrong code" and "pending session gone" come back as a 401. Only
      // the latter invalidates the token we are holding, so only the latter
      // sends the user back to the password step — a typo must not cost them
      // their place. watchman's message is the only thing that separates them.
      const message = handleApiError(error);
      if (/two-factor session expired/i.test(message)) {
        onExpired();
        return;
      }
      setServerError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-[28px] font-bold text-foreground mb-2">Two-step verification</h1>
        <p className="text-sm text-muted-foreground">
          Enter the 6-digit code from your authenticator app, or one of your backup codes.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <AuthInput
          name="code"
          type="text"
          inputMode="text"
          autoComplete="one-time-code"
          autoFocus
          placeholder="123456"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            if (serverError) setServerError('');
          }}
          error={serverError || undefined}
          required
        />

        <Button type="submit" loading={isLoading} className="w-full">
          Verify
        </Button>

        <button
          type="button"
          onClick={onCancel}
          className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors">
          Use a different account
        </button>
      </form>

      <p className="text-xs text-muted-foreground text-center">
        Lost your authenticator and your backup codes? A TCS administrator can reset two-step
        verification on your account.
      </p>
    </div>
  );
};
